/**
 * Runtime feature flags (Pillar B1 / D1).
 *
 * Why a runtime fetch instead of a build-time define:
 *   - The SPA bundle is static (Vite + nginx). Baking flags into the JS means
 *     a full rebuild + redeploy every time we flip one.
 *   - Backend owns the truth anyway (it decides whether /uploads/presigned
 *     responds with 503), so the frontend should just ask.
 *
 * Contract: loadFlags() resolves once per page load and caches. Consumers call
 * `await loadFlags()` before branching, or check the cached `flags` object
 * once they know it's been loaded.
 */
import { API_BASE } from '../services/apiService';

export interface RuntimeFlags {
  r2Upload: boolean;
  /** RFC-002: flip the composer upload path from the module-level
   *  pendingUploads Map hotfix to the per-scope unified store. Defaults OFF
   *  until the staging E2E pass + 10% prod soak lands (see plan §8). */
  useUnifiedUploadStore: boolean;
  /** PR2/PR3: surface the Ultimate (ChatGPT Image 2) model in the UI.
   *  Backend `feature_chatgpt_image` mirrors this. Default OFF — Ultimate
   *  stays dark until staging QA + cost/latency soak. */
  enableChatGptImage: boolean;
  /** Staging-only: all generation calls return canned fixtures.
   *  Diagnostic flag — no UI behavior changes. Default OFF in prod. */
  mockGeneration: boolean;
  /** Privacy Center — when true, render the detailed third-party processor
   *  table; when false, render a short disclosure banner instead. Default OFF
   *  pending legal review of the recipient list (see PR #259). The banner
   *  preserves the GDPR Art.13 / PIPL §23 minimum notification — never let
   *  the page silently drop the disclosure. */
  showDataSharing: boolean;
  /** Referral cashback program is active (backend REFERRAL_ENABLED). When false
   *  (kill switch), the register page hides the invite-code field + reward
   *  disclosure so we never promise a reward the backend will silently ignore. */
  referralEnabled: boolean;
}

// Hotfix (2026-06-24): r2Upload fails OPEN (default true) to match its real prod
// state, so a /flags timeout (the 5s abort in loadFlags; slow networks / China
// market) no longer silently routes uploads to the base64 path that keys-only
// generation rejects ("部分参考图未完成 R2 上传"). R2 genuinely off is a rare ops
// kill-switch; then /uploads/presigned returns 503 and the client errors loudly,
// which beats the silent keyless dead-end.
//
// Everything else stays fail-CLOSED on purpose — including useUnifiedUploadStore
// (codex P2 on #517): it's a separate frontend rollout switch (config.py
// feature_unified_upload_store, default false) and useComposerImages locks the path
// at mount from getFlagsSync(). Defaulting it open would force the unified path during
// a flags outage — and that path uploads the ORIGINAL file and throws on the R2
// max-size gate, while the legacy path compresses first (completeUploadFile vs
// r2DirectUpload in uploadService.ts). Leaving it false keeps a flags timeout on the
// known-good legacy R2 path that still produces a key AND shrinks large phone images.
// mockGeneration must never default on; referralEnabled/showDataSharing stay off for
// legal/correctness. Not a root fix — see the storage-agnostic upload plan.
const DEFAULT_FLAGS: RuntimeFlags = {
  r2Upload: true,
  useUnifiedUploadStore: false,
  enableChatGptImage: false,
  mockGeneration: false,
  showDataSharing: false,
  referralEnabled: false,
};

let _flags: RuntimeFlags = { ...DEFAULT_FLAGS };
let _loadPromise: Promise<RuntimeFlags> | null = null;
// True when referralEnabled is NOT trustworthily known — either the /flags fetch
// FAILED (network/5xx → DEFAULT_FLAGS) or it returned 200 without the field.
// Lets callers distinguish "the flag is settled false" (feature genuinely off)
// from "we don't actually know" (outage or missing field). The referral
// disclosure shows in the unknown case (the code is still submitted) but NOT
// when referrals are settled-off (avoids a persistent false promise).
let _loadFailed = false;

export function getFlagsSync(): RuntimeFlags {
  return _flags;
}

/** Whether referralEnabled is untrustworthy — the /flags load failed, or returned
 *  200 without the field. When true, a `false` referralEnabled means "unknown",
 *  not "settled off". */
export function flagsLoadFailed(): boolean {
  return _loadFailed;
}

export async function loadFlags(): Promise<RuntimeFlags> {
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    // Hard timeout: main.tsx `await loadFlags()` gates the React mount, so a
    // /flags request that STALLS at the network/proxy level (never rejects, never
    // resolves) would leave the SPA stuck on the empty prerender shell. fetch only
    // rejects on a connection-level failure, not a black-hole hang — so bound it
    // with AbortController and fall through to DEFAULT_FLAGS (codex integration P2).
    // 5s clears cold-start/weak-network waits (~1-2s) while capping the blank shell.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      const res = await fetch(`${API_BASE}/flags`, {
        credentials: 'include',
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`flags fetch failed: ${res.status}`);
      const data = await res.json();
      // A 200 that OMITS referralEnabled (a backend predating the feature, or a
      // future regression that drops the field) must read as "unknown", NOT
      // settled-off: Boolean(undefined) is false, which would suppress the FTC
      // disclosure while the register code is still submitted. Flag it as the
      // same "we don't actually know" signal the disclosure reads (#487 P3).
      if (data.referralEnabled === undefined) _loadFailed = true;
      _flags = {
        r2Upload: Boolean(data.r2Upload),
        useUnifiedUploadStore: Boolean(data.useUnifiedUploadStore),
        enableChatGptImage: Boolean(data.enableChatGptImage),
        mockGeneration: Boolean(data.mockGeneration),
        showDataSharing: Boolean(data.showDataSharing),
        referralEnabled: Boolean(data.referralEnabled),
      };
    } catch (e) {
      // Network-off / 5xx / timeout abort: treat every flag as OFF. Features
      // degrade but the app still boots. This is deliberate — a dead or hung
      // /flags endpoint must never take down the SPA.
      console.warn('[PopWiz] flags load failed, using defaults', e);
      _flags = { ...DEFAULT_FLAGS };
      _loadFailed = true;
    } finally {
      clearTimeout(timeout);
    }
    return _flags;
  })();
  return _loadPromise;
}
